export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  noindex?: boolean;
  structuredData?: object;
}

export const defaultSEO: SEOProps = {
  title: 'The United Travel - Premium Corporate Transportation Solutions',
  description: 'Premium corporate transportation solutions with luxury vehicles, professional chauffeurs, and 24/7 support. Book airport transfers, corporate travel, and event transportation.',
  keywords: 'corporate transportation, luxury car service, airport transfer, executive travel, business transportation, chauffeur service, premium vehicles',
  image: '/images/hero/hero-background.jpg',
  url: 'https://www.theunitedtravel.com',
  type: 'website',
  author: 'The United Travel',
  noindex: false,
};

export const pageSEO: Record<string, SEOProps> = {
  '/': {
    title: 'The United Travel - Premium Corporate Transportation Solutions',
    description: 'Premium corporate transportation solutions with luxury vehicles, professional chauffeurs, and 24/7 support. Book airport transfers, corporate travel, and event transportation.',
    keywords: 'corporate transportation, luxury car service, airport transfer, executive travel, business transportation, chauffeur service',
  },
  '/about': {
    title: 'About Us - The United Travel | Corporate Transportation Experts',
    description: 'Learn about The United Travel, your trusted partner for premium corporate transportation. Professional chauffeurs, luxury fleet, and exceptional service.',
    keywords: 'about us, corporate transportation, premium service, professional chauffeurs, luxury vehicles',
  },
  '/services': {
    title: 'Our Services - Corporate Transportation Services | The United Travel',
    description: 'Comprehensive corporate transportation services including airport transfers, executive travel, event transportation, and hourly services. Tailored solutions for your business.',
    keywords: 'airport transfer, executive travel, event transportation, hourly service, corporate travel services',
  },
  '/fleet': {
    title: 'Our Fleet - Luxury Vehicles | The United Travel',
    description: 'Explore our premium fleet of luxury vehicles including sedans, SUVs, vans, and buses. Modern, comfortable, and professionally maintained for your corporate needs.',
    keywords: 'luxury vehicles, corporate fleet, luxury sedan, executive SUV, luxury van, premium transportation vehicles',
  },
  '/booking': {
    title: 'Book Your Ride - Corporate Transportation Booking | The United Travel',
    description: 'Book your corporate transportation online. Choose your vehicle, date, and time. Secure booking with instant confirmation.',
    keywords: 'book transportation, corporate booking, luxury car booking, online booking, transportation reservation',
  },
  '/contact': {
    title: 'Contact Us - Get In Touch | The United Travel',
    description: 'Contact The United Travel for your corporate transportation needs. 24/7 customer support, dedicated account managers, and personalized service.',
    keywords: 'contact us, customer support, corporate transportation contact, get a quote',
  },
  '/testimonials': {
    title: 'Client Testimonials - The United Travel',
    description: 'Read what our corporate clients say about our premium transportation services. Trusted by leading businesses worldwide.',
    keywords: 'testimonials, reviews, client feedback, corporate transportation reviews',
  },
  '/faqs': {
    title: 'Frequently Asked Questions - The United Travel',
    description: 'Find answers to common questions about our corporate transportation services, booking process, fleet, and policies.',
    keywords: 'FAQ, frequently asked questions, corporate transportation questions, help',
  },
  '/terms': {
    title: 'Terms of Service - The United Travel',
    description: 'Read our terms of service for corporate transportation bookings. Understand policies, responsibilities, and service agreements.',
    keywords: 'terms of service, legal, policies, booking terms',
  },
  '/privacy': {
    title: 'Privacy Policy - The United Travel',
    description: 'Learn how The United Travel protects your personal information. Our commitment to privacy and data security.',
    keywords: 'privacy policy, data protection, security, GDPR',
  },
  '/login': {
    title: 'Login - Corporate Dashboard | The United Travel',
    description: 'Access your corporate transportation dashboard. Manage bookings, view history, and access exclusive features.',
    noindex: true,
  },
  '/register': {
    title: 'Register - Corporate Account | The United Travel',
    description: 'Create your corporate account to access exclusive transportation features and manage your bookings.',
    noindex: true,
  },
};

export const generateStructuredData = (path: string): object => {
  const baseStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'The United Travel',
    description: 'Premium corporate transportation solutions with luxury vehicles and professional chauffeurs',
    url: 'https://www.theunitedtravel.com',
    telephone: '+1-555-123-4567', // Update with actual phone number
    email: 'info@theunitedtravel.com', // Update with actual email
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Your Street Address', // Update with actual address
      addressLocality: 'Your City', // Update with actual city
      addressRegion: 'Your State', // Update with actual state
      postalCode: 'Your ZIP', // Update with actual ZIP
      addressCountry: 'US',
    },
    sameAs: [
      // Add social media links here
    ],
    priceRange: '$$$',
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
  };

  switch (path) {
    case '/services':
      return {
        ...baseStructuredData,
        '@type': 'Service',
        serviceType: 'Corporate Transportation',
        provider: {
          '@type': 'Organization',
          name: 'The United Travel',
        },
      };
    case '/fleet':
      return {
        '@context': 'https://schema.org',
        '@type': 'Car',
        brand: 'Various Luxury Brands',
        description: 'Premium fleet of luxury vehicles for corporate transportation',
      };
    default:
      return baseStructuredData;
  }
};

